// Filtering the book table. Pure functions over rows and a filter state object -- nothing
// here reads the document, which is the only real change from the pre-migration version.
//
// Three decisions shape the whole file, and they are unchanged:
//
// * **Filters combine, and values in one dimension union.** The control this replaced was a
//   single select over six unrelated predicates, so "not yet contacted *and* on a paid plan"
//   -- the most obvious question a strategist has -- could not be asked at all. Each filter is
//   independent and they intersect. A second value in the *same* filter unions rather than
//   replaces: "United States or United Kingdom" is that question asked inside one dimension.
// * **One button, not a row of dropdowns.** Six permanently-visible selects spent a band of
//   chrome on every page load to serve a control most people touch occasionally.
// * **An absent value is a visible answer, never a silent exclusion.** Every field behind a
//   filter is missing on some of the book: country on 32 accounts, employees on 16. Dropping
//   those rows quietly is what makes somebody stop trusting the view, so each filter that can
//   be blank offers an explicit "Not recorded", and `coverageNote` says what the field covers.
//
// **The strategist owner is not one of these.** It is a *scope*: it redraws the headline tiles
// and answers "whose book am I looking at". The filters narrow within that scope and leave the
// tiles alone.
//
// The **AE** filter is not that, despite reading like it. Two different people own an account:
// the strategist doing the outbound (`SDR_OWNER_C`, which is the scope) and the AE who owns the
// account itself (`OWNER_ID`, which is this). Filtering to one AE inside a strategist's book is
// a real question -- "which of my accounts is Rahaf on" -- and it narrows rather than rescopes,
// so it belongs here and the tiles stay put.

import type { BookAccount, Opportunity, WhyNowKind } from '../api/types';
import { formatShortDay, pacificCalendarDay, titleCase } from '../lib/format.ts';
import { meetingBooked } from './boardPref.ts';
import { inPool, inSprint, poolChoices, sprintChoices } from './sprints.ts';
import type { PoolChoice } from './sprints.ts';
import { lastOutbound } from './derived.ts';
import type { RankedAccount } from './derived.ts';
import {
  STAGE_LABELS, STAGE_ORDER, fundShortName, fundingStage, fundsInScope, hasTieToFund,
  readRelationship, roundIsRecent,
} from './funding.ts';

// Buckets rather than a slider. 217 of the 314 accounts sit under 50 employees, so the useful
// question is almost always "show me the ones that are not tiny".
const EMPLOYEE_BUCKETS = [
  { value: '1-49', label: '1–49', test: (count: number) => count < 50 },
  { value: '50-199', label: '50–199', test: (count: number) => count >= 50 && count < 200 },
  { value: '200-999', label: '200–999', test: (count: number) => count >= 200 && count < 1000 },
  { value: '1000+', label: '1,000+', test: (count: number) => count >= 1000 },
];

// Highest tier first, the way the SQL rollup ranks them -- alphabetically 'free' would beat
// 'enterprise', which is the trap the plan column already avoids.
export const TIER_ORDER = ['enterprise', 'scale', 'startup', 'pro', 'free'];

export const UNKNOWN = '__unknown__';

// The heads of the two opportunity filters, above the individual stage names. Prefixed so a Salesforce
// picklist can never collide with one -- a stage literally called "lost" would otherwise take
// over the semantic option's value.
export const ANY_LOST_DEAL = '__lost__';
export const ANY_OPEN_DEAL = '__open__';
export const NO_DEAL = '__none__';
export const OTHERS_OPEN_DEAL = '__others_open__';

export interface DateRangeFilter {
  from: string;
  to: string;
}

// Every dimension holds a list so two countries (or two plans, two AEs) can sit together.
// Search stays a string: it is a text box, not a pick-list, and it already has its own chip.
// Date ranges are a pair of bounds, not a pick-list, so they sit beside the arrays.
export interface FilterState {
  sprint: string[];
  pool: string[];
  activity: string[];
  meeting: string[];
  reply: string[];
  opportunity: string[];
  past_opportunity: string[];
  plan: string[];
  employees: string[];
  country: string[];
  clay: string[];
  ae: string[];
  tag: string[];
  funding_stage: string[];
  funding_recent: string[];
  investor: string[];
  search: string;
  added: DateRangeFilter;
  contacted: DateRangeFilter;
}

export type FilterDimension = keyof Omit<FilterState, 'search' | 'added' | 'contacted'>;

export function emptyDateRange(): DateRangeFilter {
  return { from: '', to: '' };
}

// Always a fresh object graph. EMPTY_FILTERS used to be a shared constant, and after
// dates (and arrays) live nested on it, one in-place mutation would poison every reset.
export function emptyFilters(search = ''): FilterState {
  return {
    sprint: [], pool: [], activity: [], meeting: [], reply: [], opportunity: [], past_opportunity: [], plan: [], employees: [],
    country: [], clay: [], ae: [], tag: [], funding_stage: [], funding_recent: [], investor: [], search,
    added: emptyDateRange(),
    contacted: emptyDateRange(),
  };
}

// Append, never replace -- picking United Kingdom after United States is how a second value
// gets onto the chip row. A duplicate is a no-op so the select snapping back to empty cannot
// stack the same country twice if someone opens it and confirms the same option.
export function addFilterValue(
  filters: FilterState,
  key: FilterDimension,
  value: string,
): FilterState {
  if (!value) return filters;
  const current = filters[key];
  if (current.includes(value)) return filters;
  return { ...filters, [key]: [...current, value] };
}

export function removeFilterValue(
  filters: FilterState,
  key: FilterDimension,
  value: string,
): FilterState {
  return { ...filters, [key]: filters[key].filter(item => item !== value) };
}

export const TAG_KINDS: WhyNowKind[] = [
  'signal', 'usage_stopped', 'usage_climbing', 'overage', 'tier', 'note', 'quiet',
];

const TAG_LABELS: Record<WhyNowKind, string> = {
  signal: 'Signal',
  usage_stopped: 'Usage stopped',
  usage_climbing: 'Usage climbing',
  overage: 'Overage',
  tier: 'Tier',
  note: 'Note',
  quiet: 'Quiet',
};

// Every option carries three things: the value the predicate matches on, the `label` shown in
// the panel -- which includes a count, so picking one is never a guess about what comes back --
// and the shorter `chip` used once it is active. "United States (107)" is right in a dropdown
// and too long on a chip.
export interface FilterOption {
  value: string;
  label: string;
  chip: string;
}

function countWhere(rows: BookAccount[], test: (row: BookAccount) => boolean): number {
  return rows.filter(test).length;
}

export function clayTier(row: BookAccount): string | null {
  const engine = row.clay?.engine;
  if (!engine || engine.tier == null || engine.tier === '') return null;
  return String(engine.tier);
}

// --- Options --------------------------------------------------------------------------------
// Derived from the rows in scope, never hard-coded. That matters most for the Clay tier: four
// tier values have ever been written and the block exists on a handful of accounts, so a list
// of bands built from that sample would be wrong within a month of the workflows running at
// volume. Country has the same shape for a different reason -- a long tail that grows weekly.

function activityOptions(rows: BookAccount[]): FilterOption[] {
  return [
    { value: 'untouched', chip: 'Not yet contacted',
      label: `Not yet contacted (${countWhere(rows, row => row.touches === 0)})` },
    { value: 'touched', chip: 'Contacted',
      label: `Contacted (${countWhere(rows, row => row.touches > 0)})` },
    { value: 'collision', chip: 'Worked by someone else',
      label: `Worked by someone else (${countWhere(rows, row => Boolean(row.other_touches))})` },
  ];
}

// --- Opportunities ---------------------------------------------------------------------
// Two questions that the old single Deals control mixed up (Vikram, 29 Sep, GRO-159):
//
// * **Opportunity** (Outreach): what is open that *this strategist* is responsible for. A deal
//   counts when it was created on or after the owner's first touch on the account, which is meant
//   to mirror the Activity tab's qualified pipeline (`activity._credit_opportunities`,
//   "influenced"). An AE's deal from before any outreach is not the strategist's pipeline, but
//   it is still a live conversation, so it has its own option ("Open, someone else's") and
//   "None of yours open" says exactly what it means rather than reading as "cold".
//
//   The date is `row.first_sent_touch`, the owner's first *sent* touch matched by email, which is
//   what `_credit_opportunities` dates from (GRO-265). `first_touch` (any direction) is not used,
//   so a prospect writing in before the AE opens a deal does not credit the strategist.
// * **Past opportunities** (Company): what this company has already been through with anyone
//   at Cartesia -- closed lost, disqualified, passed to self-serve, won. History, not pipeline.
//
// An account carries a *list* of deals, so every predicate asks "does this account have a deal
// at this stage", never "is its stage this".

export function strategistsOpportunities(row: BookAccount): Opportunity[] {
  const firstTouch = row.first_sent_touch;
  if (!firstTouch) return [];
  return row.opportunities.filter(deal => Boolean(deal.created && deal.created >= firstTouch));
}

function openOfTheirs(row: BookAccount): Opportunity[] {
  return strategistsOpportunities(row).filter(deal => !deal.closed);
}

// Open deals on the account that are not credited to the strategist: an AE's from before the
// first touch, or any open deal on an account they have never touched.
function openOfOthers(row: BookAccount): Opportunity[] {
  const theirs = new Set(openOfTheirs(row));
  return row.opportunities.filter(deal => !deal.closed && !theirs.has(deal));
}

function pastDeals(row: BookAccount): Opportunity[] {
  return row.opportunities.filter(deal => Boolean(deal.closed));
}

function stagesOf(deals: Opportunity[]): string[] {
  const stages = new Set<string>();
  deals.forEach(deal => { if (deal.stage) stages.add(deal.stage); });
  return [...stages].sort();
}

function opportunityOptions(rows: BookAccount[]): FilterOption[] {
  const open = countWhere(rows, row => openOfTheirs(row).length > 0);
  const none = rows.length - open;
  const options: FilterOption[] = [];
  if (open) options.push({ value: ANY_OPEN_DEAL, chip: 'Any open', label: `Any open opportunity (${open})` });
  const stages = stagesOf(rows.flatMap(openOfTheirs));
  for (const stage of stages) {
    options.push({ value: stage, chip: stage, label: `${stage} (${countWhere(rows,
      row => openOfTheirs(row).some(deal => deal.stage === stage))})` });
  }
  const others = countWhere(rows, row => openOfOthers(row).length > 0);
  if (others) {
    options.push({ value: OTHERS_OPEN_DEAL, chip: 'Open, someone else’s',
      label: `Open, someone else’s (${others})` });
  }
  if (none) options.push({ value: NO_DEAL, chip: 'None of yours open', label: `None of yours open (${none})` });
  return options;
}

function matchesOpportunity(row: BookAccount, value: string): boolean {
  const open = openOfTheirs(row);
  if (value === ANY_OPEN_DEAL) return open.length > 0;
  if (value === OTHERS_OPEN_DEAL) return openOfOthers(row).length > 0;
  if (value === NO_DEAL) return open.length === 0;
  if (!value) return true;
  return open.some(deal => deal.stage === value);
}

function pastOpportunityOptions(rows: BookAccount[]): FilterOption[] {
  const lost = countWhere(rows, row => pastDeals(row).some(deal => !deal.won));
  const none = countWhere(rows, row => pastDeals(row).length === 0);
  const options: FilterOption[] = [];
  if (lost) options.push({ value: ANY_LOST_DEAL, chip: 'Previously lost', label: `Previously lost (${lost})` });
  for (const stage of stagesOf(rows.flatMap(pastDeals))) {
    options.push({ value: stage, chip: stage, label: `${stage} (${countWhere(rows,
      row => pastDeals(row).some(deal => deal.stage === stage))})` });
  }
  if (none) options.push({ value: NO_DEAL, chip: 'No past opportunity', label: `None (${none})` });
  return options;
}

// Deliberately all-time, where the *Lost MM/YY* flag on the row is capped at 24 months: the flag
// says "this changes how you open the account today", the filter "show me who we have lost".
function matchesPastOpportunity(row: BookAccount, value: string): boolean {
  const past = pastDeals(row);
  if (value === ANY_LOST_DEAL) return past.some(deal => !deal.won);
  if (value === NO_DEAL) return past.length === 0;
  if (!value) return true;
  return past.some(deal => deal.stage === value);
}

// The account's AE, from `ACCOUNT.OWNER_ID` -- a different person from the strategist who
// owns the outbound, which is what the owner scope selects on. Alphabetical rather than by
// count, for the reason Country is: a native select is navigated by typing a first letter.
function accountOwnerOptions(rows: BookAccount[]): FilterOption[] {
  const names = new Set<string>();
  rows.forEach(row => { if (row.account_owner) names.add(row.account_owner); });
  const missing = countWhere(rows, row => !row.account_owner);
  const owners = [...names].sort().map(owner => ({
    value: owner,
    chip: owner,
    label: `${owner} (${countWhere(rows, row => row.account_owner === owner)})`,
  }));
  // Every account on the book carries an AE today, so this option normally does not render.
  // It is conditional rather than absent because ownership is edited in Salesforce, and a
  // gap appearing there must show up as a bucket rather than as rows quietly dropping out.
  if (!missing) return owners;
  return [...owners,
    { value: UNKNOWN, chip: 'No AE recorded', label: `Not recorded (${missing})` }];
}

function planOptions(rows: BookAccount[]): FilterOption[] {
  // No separate "Not recorded" here, deliberately: an account has a tier exactly when it has a
  // playground org, so a third option would be the same accounts as "No playground org" under
  // a vaguer name.
  const tiers = TIER_ORDER.filter(tier => rows.some(row => row.tier === tier));
  const tierOptions = tiers.map(tier => ({
    value: tier,
    chip: titleCase(tier),
    label: `${titleCase(tier)} (${countWhere(rows, row => row.tier === tier)})`,
  }));
  return [
    { value: 'any', chip: 'Has an org',
      label: `Has a playground org (${countWhere(rows, row => row.org_count > 0)})` },
    { value: 'paid', chip: 'Paid plan',
      label: `On a paid plan (${countWhere(rows, row => Boolean(row.tier) && row.tier !== 'free')})` },
    ...tierOptions,
    { value: 'none', chip: 'No org',
      label: `No playground org (${countWhere(rows, row => !row.org_count)})` },
  ];
}

function employeeOptions(rows: BookAccount[]): FilterOption[] {
  const present = EMPLOYEE_BUCKETS.filter(bucket =>
    rows.some(row => row.employee_count != null && bucket.test(row.employee_count)));
  const missing = countWhere(rows, row => row.employee_count == null);
  const buckets = present.map(bucket => ({
    value: bucket.value,
    chip: `${bucket.label} employees`,
    label: `${bucket.label} (${countWhere(rows,
      row => row.employee_count != null && bucket.test(row.employee_count))})`,
  }));
  if (!missing) return buckets;
  return [...buckets,
    { value: UNKNOWN, chip: 'Size not recorded', label: `Not recorded (${missing})` }];
}

function countryOptions(rows: BookAccount[]): FilterOption[] {
  const names = new Set<string>();
  rows.forEach(row => { if (row.country) names.add(row.country); });
  const missing = countWhere(rows, row => !row.country);
  // Alphabetical rather than by size: a native select is navigated by typing the first letter,
  // and re-ordering by frequency breaks that.
  const countries = [...names].sort().map(country => ({
    value: country,
    chip: country,
    label: `${country} (${countWhere(rows, row => row.country === country)})`,
  }));
  if (!missing) return countries;
  return [...countries,
    { value: UNKNOWN, chip: 'Country not recorded', label: `Not recorded (${missing})` }];
}

// The band's human name, preferring the label the workflow wrote. `tier_label` lives in the
// hand-rolled metadata blob and can be absent when that fails to parse, so the fallback builds
// the same shape rather than leaving a bare "1" in a dropdown.
function clayTierLabel(rows: BookAccount[], tier: string): string {
  const match = rows.find(row => clayTier(row) === tier && row.clay?.engine?.tier_label);
  return match?.clay.engine?.tier_label ?? `Tier ${tier}`;
}

function clayTierOptions(rows: BookAccount[]): FilterOption[] {
  const tiers = new Set<string>();
  rows.forEach(row => { const tier = clayTier(row); if (tier) tiers.add(tier); });
  const missing = countWhere(rows, row => clayTier(row) == null);
  const bands = [...tiers].sort().map(tier => ({
    value: tier,
    chip: clayTierLabel(rows, tier),
    label: `${clayTierLabel(rows, tier)} (${countWhere(rows, row => clayTier(row) === tier)})`,
  }));
  if (!missing) return bands;
  return [...bands,
    { value: UNKNOWN, chip: 'No Clay tier', label: `Not assessed (${missing})` }];
}

// --- Predicates -----------------------------------------------------------------------------
function matchesActivity(row: BookAccount, value: string): boolean {
  if (value === 'untouched') return row.touches === 0;
  if (value === 'touched') return row.touches > 0;
  if (value === 'collision') return Boolean(row.other_touches);
  return true;
}

// Vikram wrote "in communication or reply received". The book has no separate
// "in communication" bit beyond a reply, so this is one filter: anything inbound to the
// owner on any channel, auto-replies excluded. An answered inbound call counts, the same as
// the Activity page's In conversation. "The owner" is the book's, not the viewer's: an
// admin viewing Omair's book sees replies to Omair.
export function replyReceived(row: BookAccount): boolean {
  return (row.responses ?? 0) > 0;
}

// The same, to anyone at Cartesia: another strategist, an AE, Eva. The account is in a
// conversation even when the owner is not the one in it. The owner test is ORed in so a
// cached payload from before `any_responses` existed still counts the owner's own replies.
export function replyReceivedByAnyone(row: BookAccount): boolean {
  return replyReceived(row) || (row.any_responses ?? 0) > 0;
}

function yesNoOptions(
  rows: BookAccount[],
  test: (row: BookAccount) => boolean,
  yesChip: string,
  noChip: string,
): FilterOption[] {
  const yes = countWhere(rows, test);
  const no = rows.length - yes;
  return [
    { value: 'yes', chip: yesChip, label: `Yes (${yes})` },
    { value: 'no', chip: noChip, label: `No (${no})` },
  ];
}

// Sprint and Pool (`sprints.ts`, the same options and matching as All accounts'): one option per
// sprint, or per other pool, on the rows in scope, valued by its key. None in scope leaves no
// options, and the panel then hides the control. High priority is not here: the bar's chip is it.
function choiceOptions(choices: PoolChoice[]): FilterOption[] {
  return choices.map(choice => ({ value: choice.key, chip: choice.label, label: `${choice.label} (${choice.count})` }));
}

function sprintOptions(rows: BookAccount[]): FilterOption[] {
  return choiceOptions(sprintChoices(rows));
}

function poolOptions(rows: BookAccount[]): FilterOption[] {
  return choiceOptions(poolChoices(rows));
}

function meetingOptions(rows: BookAccount[]): FilterOption[] {
  return yesNoOptions(rows, meetingBooked, 'Yes', 'No');
}

// Three options, not a yes/no pair. "To anyone" includes "to the strategist", so the two
// overlap on purpose; "No reply" is the complement of the wider one.
function replyOptions(rows: BookAccount[]): FilterOption[] {
  const toStrategist = countWhere(rows, replyReceived);
  const toAnyone = countWhere(rows, replyReceivedByAnyone);
  const noReply = rows.length - toAnyone;
  return [
    { value: 'strategist', chip: 'To the strategist', label: `To the strategist (${toStrategist})` },
    { value: 'anyone', chip: 'To anyone', label: `To anyone (${toAnyone})` },
    { value: 'none', chip: 'No reply', label: `No reply (${noReply})` },
  ];
}

function matchesYesNo(isYes: boolean, value: string): boolean {
  if (value === 'yes') return isYes;
  if (value === 'no') return !isYes;
  return true;
}

function matchesMeeting(row: BookAccount, value: string): boolean {
  return matchesYesNo(meetingBooked(row), value);
}

function matchesReply(row: BookAccount, value: string): boolean {
  if (value === 'strategist') return replyReceived(row);
  if (value === 'anyone') return replyReceivedByAnyone(row);
  if (value === 'none') return !replyReceivedByAnyone(row);
  return true;
}

function matchesAccountOwner(row: BookAccount, value: string): boolean {
  if (value === UNKNOWN) return !row.account_owner;
  if (!value) return true;
  return row.account_owner === value;
}

// Plan does double duty on purpose: "has an org at all" and "is on this tier" are the same
// question asked at two depths, and splitting them would put two controls side by side where
// one answers both.
function matchesPlan(row: BookAccount, value: string): boolean {
  if (value === 'any') return row.org_count > 0;
  if (value === 'none') return !row.org_count;
  if (value === 'paid') return Boolean(row.tier) && row.tier !== 'free';
  if (value) return row.tier === value;
  return true;
}

function matchesEmployees(row: BookAccount, value: string): boolean {
  if (value === UNKNOWN) return row.employee_count == null;
  if (!value) return true;
  if (row.employee_count == null) return false;
  const bucket = EMPLOYEE_BUCKETS.find(candidate => candidate.value === value);
  return bucket ? bucket.test(row.employee_count) : true;
}

function matchesCountry(row: BookAccount, value: string): boolean {
  if (value === UNKNOWN) return !row.country;
  if (!value) return true;
  return row.country === value;
}

function matchesClayTier(row: BookAccount, value: string): boolean {
  if (value === UNKNOWN) return clayTier(row) == null;
  if (!value) return true;
  return clayTier(row) === value;
}

function rowTagKind(row: BookAccount): string | null {
  const ranked = row as RankedAccount;
  return ranked.tag?.kind ?? null;
}

function tagOptions(rows: BookAccount[]): FilterOption[] {
  const options: FilterOption[] = [];
  for (const kind of TAG_KINDS) {
    const count = countWhere(rows, row => rowTagKind(row) === kind);
    if (!count) continue;
    options.push({
      value: kind,
      chip: TAG_LABELS[kind],
      label: `${TAG_LABELS[kind]} (${count})`,
    });
  }
  return options;
}

function matchesTag(row: BookAccount, value: string): boolean {
  if (!value) return true;
  return rowTagKind(row) === value;
}

// --- Funding & investors (GRO-159) ---------------------------------------------------------
// Their own row in the panel. Options come from the Apollo payload joined onto the rows, so a
// board whose funding has not been fetched yet offers only "Couldn't find out" — which is the
// honest answer rather than an empty control.

// A prefix no fund name can take, for the two coarse investor options above the fund list.
export const ANY_OUR_FUND = '__any_fund__';
export const RECENT_ROUND_TIE = '__recent_tie__';
export const NO_OUR_FUND = '__no_fund__';

function fundingStageOptions(rows: BookAccount[]): FilterOption[] {
  const options: FilterOption[] = [];
  for (const stage of STAGE_ORDER) {
    const count = countWhere(rows, row => fundingStage(row) === stage);
    if (!count) continue;
    options.push({ value: stage, chip: STAGE_LABELS[stage], label: `${STAGE_LABELS[stage]} (${count})` });
  }
  const unknown = countWhere(rows, row => fundingStage(row) === 'unknown');
  if (!unknown) return options;
  return [...options,
    { value: UNKNOWN, chip: 'Funding unknown', label: `${STAGE_LABELS.unknown} (${unknown})` }];
}

function matchesFundingStage(row: BookAccount, value: string): boolean {
  if (value === UNKNOWN) return fundingStage(row) === 'unknown';
  if (!value) return true;
  return fundingStage(row) === value;
}

function fundingRecentOptions(rows: BookAccount[], today = ''): FilterOption[] {
  const count = countWhere(rows, row => roundIsRecent(row, today));
  return [{ value: 'yes', chip: 'Raised in the last 90 days', label: `Yes (${count})` }];
}

function matchesFundingRecent(row: BookAccount, value: string, today = ''): boolean {
  if (value === 'yes') return roundIsRecent(row, today);
  return true;
}

function investorOptions(rows: BookAccount[]): FilterOption[] {
  const any = countWhere(rows, row => readRelationship(row).kind === 'funds');
  const recent = countWhere(rows, row => readRelationship(row).ties.some(tie => tie.strength <= 2));
  const none = countWhere(rows, row => readRelationship(row).kind === 'none');
  const unknown = countWhere(rows, row => readRelationship(row).kind === 'unknown');

  const options: FilterOption[] = [];
  if (any) options.push({ value: ANY_OUR_FUND, chip: 'Any of our funds', label: `Any of our funds (${any})` });
  if (recent) {
    options.push({ value: RECENT_ROUND_TIE, chip: 'Recent round',
      label: `A fund in a recent round (${recent})` });
  }
  for (const fund of fundsInScope(rows)) {
    options.push({ value: fund, chip: fundShortName(fund),
      label: `${fund} (${countWhere(rows, row => hasTieToFund(row, fund))})` });
  }
  if (none) options.push({ value: NO_OUR_FUND, chip: 'None of our funds', label: `None of our funds (${none})` });
  if (unknown) options.push({ value: UNKNOWN, chip: 'Investors unknown', label: `Couldn’t find out (${unknown})` });
  return options;
}

function matchesInvestor(row: BookAccount, value: string): boolean {
  const reading = readRelationship(row);
  if (value === ANY_OUR_FUND) return reading.kind === 'funds';
  if (value === RECENT_ROUND_TIE) return reading.ties.some(tie => tie.strength <= 2);
  if (value === NO_OUR_FUND) return reading.kind === 'none';
  if (value === UNKNOWN) return reading.kind === 'unknown';
  if (!value) return true;
  return hasTieToFund(row, value);
}

// --- Date ranges ----------------------------------------------------------------------------
// Two controls FILTERS cannot describe: date added (when the account landed on this book)
// and date last contacted (the same day the Last out column shows). Inclusive at both
// ends, Pacific calendar days, open on a bound that is empty. An inverted pair is swapped
// rather than matching nothing.
//
// There is no "never contacted" option on the last-contacted range. Activity → Not yet
// contacted already answers that, and a second control for the same pile would read as
// two ways to ask one question. A last-contacted range therefore drops unstamped rows,
// and coverageNote says so.

export function isDateRangeActive(range: DateRangeFilter): boolean {
  return Boolean(range.from || range.to);
}

export function dateRangeChip(range: DateRangeFilter): string {
  if (range.from && range.to) {
    const start = range.from <= range.to ? range.from : range.to;
    const end = range.from <= range.to ? range.to : range.from;
    return `${formatShortDay(start)} – ${formatShortDay(end)}`;
  }
  if (range.from) return `from ${formatShortDay(range.from)}`;
  return `until ${formatShortDay(range.to)}`;
}

function orderedDateRange(range: DateRangeFilter): DateRangeFilter {
  if (range.from && range.to && range.from > range.to) {
    return { from: range.to, to: range.from };
  }
  return range;
}

function calendarDay(value: string | null | undefined): string | null {
  if (!value) return null;
  return pacificCalendarDay(value);
}

function dayInRange(day: string | null, range: DateRangeFilter): boolean {
  if (!isDateRangeActive(range)) return true;
  if (!day) return false;
  const bounds = orderedDateRange(range);
  if (bounds.from && day < bounds.from) return false;
  if (bounds.to && day > bounds.to) return false;
  return true;
}

function addedDay(row: BookAccount): string | null {
  // `added_at`: Scout assigned-at when the account landed on this book, else the door's first
  // knock, else the signal's first sighting, else Salesforce CreatedDate. One definition,
  // computed in book.py, shared with bank health. Missing only when none of the four exist.
  return calendarDay(row.added_at);
}

function contactedDay(row: BookAccount): string | null {
  return calendarDay(lastOutbound(row)?.day);
}

export interface DateRangeFilterSpec {
  key: 'added' | 'contacted';
  label: string;
  hint: string;
  dayOf: (row: BookAccount) => string | null;
}

export const DATE_RANGE_FILTERS: DateRangeFilterSpec[] = [
  {
    key: 'added',
    label: 'Date added',
    hint: 'When the account landed on this book, or entered Scout for bank rows.',
    dayOf: addedDay,
  },
  {
    key: 'contacted',
    label: 'Last contacted',
    hint: 'The Last out day: the most recent outbound touch by anybody.',
    dayOf: contactedDay,
  },
];

// Every filter described once: the key it holds in state, what it is called, how its options
// are built, and how it matches. Adding a filter is one entry here rather than an edit in five
// places.
export type FilterGroupKey = 'pools' | 'outreach' | 'scout' | 'company' | 'funding';

export interface FilterSpec {
  key: FilterDimension;
  label: string;
  // Which labelled row of the panel the control sits in.
  group: FilterGroupKey;
  build: (rows: BookAccount[], today?: string) => FilterOption[];
  match: (row: BookAccount, value: string, today?: string) => boolean;
}

// The panel's labelled rows, in order: the sprint and pool the account is in,
// then what we have done here, Scout's own work and tag state,
// who the company is and who owns it, then how it is funded and by whom of ours. The dates
// follow as their own row (DATE_RANGE_FILTERS).
export const FILTER_GROUPS: { key: FilterGroupKey; label: string }[] = [
  { key: 'pools', label: 'Sprint & pool' },
  { key: 'outreach', label: 'Outreach' },
  { key: 'scout', label: 'Scout' },
  { key: 'company', label: 'Company' },
  { key: 'funding', label: 'Funding & investors' },
];

export const FILTERS: FilterSpec[] = [
  { key: 'sprint', label: 'Sprint', group: 'pools', build: sprintOptions, match: inSprint },
  { key: 'pool', label: 'Pool', group: 'pools', build: poolOptions, match: inPool },
  { key: 'activity', label: 'Activity', group: 'outreach', build: activityOptions, match: matchesActivity },
  { key: 'meeting', label: 'Meeting booked', group: 'outreach', build: meetingOptions, match: matchesMeeting },
  { key: 'reply', label: 'Reply received', group: 'outreach', build: replyOptions, match: matchesReply },
  { key: 'opportunity', label: 'Opportunity', group: 'outreach',
    build: opportunityOptions, match: matchesOpportunity },
  { key: 'tag', label: 'Why now', group: 'scout', build: tagOptions, match: matchesTag },
  { key: 'plan', label: 'Plan', group: 'company', build: planOptions, match: matchesPlan },
  { key: 'past_opportunity', label: 'Past opportunities', group: 'company',
    build: pastOpportunityOptions, match: matchesPastOpportunity },
  { key: 'employees', label: 'Employees', group: 'company', build: employeeOptions, match: matchesEmployees },
  { key: 'country', label: 'Country', group: 'company', build: countryOptions, match: matchesCountry },
  { key: 'clay', label: 'Clay tier', group: 'company', build: clayTierOptions, match: matchesClayTier },
  { key: 'ae', label: 'AE', group: 'company', build: accountOwnerOptions, match: matchesAccountOwner },
  { key: 'funding_stage', label: 'Funding stage', group: 'funding',
    build: fundingStageOptions, match: matchesFundingStage },
  { key: 'funding_recent', label: 'Raised recently', group: 'funding',
    build: fundingRecentOptions, match: matchesFundingRecent },
  { key: 'investor', label: 'VC partner', group: 'funding',
    build: investorOptions, match: matchesInvestor },
];

// Date added and last contacted live in DATE_RANGE_FILTERS, not here: they are a pair of
// inclusive day bounds rather than a select, so FilterPanel renders them as date inputs.

// Shared Slack was one of these once and is deliberately gone. It is one bit on 13 of 422
// accounts, it is already a mark on the account name where it has to stop a cold email before
// anybody opens a panel, and as a *filter* it only ever answered a question nobody asks —
// "show me the thirteen companies we already talk to daily".


export function applyFilters<Row extends BookAccount>(
  rows: Row[],
  filters: FilterState,
  today = '',
): Row[] {
  const query = filters.search.toLowerCase().trim();
  return rows.filter(row => {
    for (const filter of FILTERS) {
      const values = filters[filter.key];
      // Empty means the dimension is idle. Otherwise any one selected value is enough --
      // two countries include either, then the next dimension still has to pass.
      if (!values.length) continue;
      if (!values.some(value => filter.match(row, value, today))) return false;
    }
    for (const rangeFilter of DATE_RANGE_FILTERS) {
      if (!dayInRange(rangeFilter.dayOf(row), filters[rangeFilter.key])) return false;
    }
    // Name or domain, as on All accounts.
    if (query && !row.account.toLowerCase().includes(query) && !(row.domain || '').toLowerCase().includes(query)) return false;
    return true;
  });
}

// How many values are narrowing the list. Two countries count as two: each is its own chip,
// and the badge has to agree with the chips or a stacked filter looks like a single one.
// The search box is not counted: it has its own visible input and its own chip, so folding
// it into the button's badge would report it twice.
export function activeFilterCount(filters: FilterState): number {
  const selects = FILTERS.reduce((total, filter) => total + filters[filter.key].length, 0);
  const ranges = DATE_RANGE_FILTERS.filter(filter => isDateRangeActive(filters[filter.key])).length;
  return selects + ranges;
}

// True when a selected value can drop rows that lack the field, and no sibling value is
// putting those rows back. "United States or not recorded" includes the gaps; the note
// would then describe a silent exclusion that is not happening.
function excludesUnrecorded(values: string[], unrecorded: string): boolean {
  if (!values.length || values.includes(unrecorded)) return false;
  return values.some(Boolean);
}

// How complete the fields behind the *active* filters are. A filter on a field that only 90%
// of the book records has to say so, or the missing 10% read as though they never existed.
export function coverageNote(rows: BookAccount[], filters: FilterState): string {
  if (!rows.length) return '';

  const notes: string[] = [];
  const record = (isActive: boolean, populated: number, label: string) => {
    if (!isActive || populated === rows.length) return;
    notes.push(`${populated} of ${rows.length} record ${label}`);
  };

  record(excludesUnrecorded(filters.employees, UNKNOWN),
    countWhere(rows, row => row.employee_count != null), 'a company size');
  record(excludesUnrecorded(filters.country, UNKNOWN),
    countWhere(rows, row => Boolean(row.country)), 'a country');
  record(excludesUnrecorded(filters.plan, 'none'),
    countWhere(rows, row => row.org_count > 0), 'a playground org');
  record(excludesUnrecorded(filters.clay, UNKNOWN),
    countWhere(rows, row => clayTier(row) != null), 'a Clay tier');
  // Silent today -- every account carries an AE, so `record` skips it. Here because the field
  // is edited in Salesforce and a gap must announce itself rather than read as an empty board.
  record(excludesUnrecorded(filters.ae, UNKNOWN),
    countWhere(rows, row => Boolean(row.account_owner)), 'an AE');
  // Scout_Assigned_At__c is missing on pre-Scout bulk loads. Say so rather than dropping
  // those rows as if they were never added.
  // Funding comes from a nightly Apollo pass: say how much of the scope it has reached.
  record(excludesUnrecorded(filters.funding_stage, UNKNOWN),
    countWhere(rows, row => fundingStage(row) !== 'unknown'), 'a funding round');
  record(excludesUnrecorded(filters.investor, UNKNOWN),
    countWhere(rows, row => readRelationship(row).kind !== 'unknown'), 'an investor list');
  record(isDateRangeActive(filters.added),
    countWhere(rows, row => addedDay(row) != null), 'a date added');
  // Never-contacted accounts have no Last out day, so a last-contacted range silently
  // excludes them. Activity → Not yet contacted is the way to select that pile.
  record(isDateRangeActive(filters.contacted),
    countWhere(rows, row => contactedDay(row) != null), 'a last-contacted date');
  // The opportunity filters get no line. The other fields are ones an account *should* have and sometimes
  // lacks; having no opportunity is the ordinary state of 398 of the 422 accounts, and
  // reporting it as thin coverage would read as a data problem rather than as the answer.
  return notes.join(' · ');
}
